import { ReactNode, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNotifications } from "@/hooks/useNotifications";
import { useMessages } from "@/hooks/useMessages";
import { Bell, MessageCircle, Search, Menu, Home, Camera, Map, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { GlobalSearch } from "./GlobalSearch";
import { LanguageSelector } from "./LanguageSelector";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "@/contexts/TranslationContext";
interface NewMainLayoutProps {
  children: ReactNode;
}
const NewMainLayout = ({
  children
}: NewMainLayoutProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const location = useLocation();
  const isChatView = location.pathname.includes('/chat/') || location.pathname === '/app/messages';
  const {
    t
  } = useTranslation();
  const {
    user,
    profile
  } = useAuth();
  const {
    unreadCount: notificationsCount
  } = useNotifications();
  const {
    unreadCount: messagesCount
  } = useMessages();

  // Navigation items per la bottom navbar
  const navigation = [{
    name: t('nav.home'),
    href: "/app",
    icon: Home
  }, {
    name: t('nav.feed'),
    href: "/app/feed",
    icon: Camera
  }, {
    name: t('nav.map'),
    href: "/app/map",
    icon: Map
  }, {
    name: t('nav.messages'),
    href: "/app/messages",
    icon: MessageCircle
  }, {
    name: t('nav.profile'),
    href: "/app/profile",
    icon: User
  }];
  const isActive = (path: string) => location.pathname === path;
  return <SidebarProvider defaultOpen={true} className="min-h-screen gradient-secondary font-inter">
      <div className="flex min-h-screen w-full">
        {/* Sidebar Desktop e Mobile */}
        <AppSidebar />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Header */}
          <header className="sticky top-0 z-50 glass-strong border-b border-border/20 backdrop-blur-2xl shadow-elevated">
            <div className="flex items-center h-16 px-6 gap-4 md:gap-6">
              {/* Sidebar Trigger and Logo - Sinistra */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <SidebarTrigger className="p-2.5 rounded-xl glass hover:glass-strong border border-border/30 hover:border-primary/50 hover:glow-primary transition-all duration-300 hover:scale-110" />
                
                {/* Logo - Hidden on mobile, visible on desktop */}
                <Link to="/app" className="hidden md:flex items-center space-x-3 transition-elastic hover:scale-105">
                  <div className="w-12 h-12 rounded-2xl glass border border-primary/30 flex items-center justify-center transition-elastic hover:scale-110 hover:glow-primary hover:border-primary/60">
                    <img src="/lovable-uploads/5e002d38-22aa-49af-85ae-34109191ea66.png" alt="AstroPet Logo" className="w-8 h-8" />
                  </div>
                  <span className="text-lg font-bold gradient-text font-space tracking-wide">AstroPet</span>
                </Link>
              </div>

              {/* Language Selector - Visibile solo su tablet e PC */}
              <div className="hidden md:flex flex-shrink-0">
                <LanguageSelector />
              </div>

              {/* Search Bar - Centro, espandibile su tablet e PC */}
              <div className="hidden md:flex flex-1 max-w-2xl">
                <GlobalSearch placeholder={t('search.placeholder')} className="w-full" />
              </div>

              {/* Right Actions - Notifiche e Messaggi - Destra */}
              <div className="flex items-center gap-3 md:gap-4 flex-shrink-0 ml-auto md:ml-0">
                {/* Language Selector Mobile */}
                <div className="md:hidden">
                  <LanguageSelector />
                </div>

                {/* Notifications */}
                <Link to="/app/notifications">
                  <Button size="sm" variant="floating" className="relative w-12 h-12 rounded-2xl glass hover:glass-strong border border-border/30 hover:border-primary/50 hover:glow-primary transition-all duration-300 group">
                    <Bell className="w-5 h-5 transition-transform group-hover:scale-110" />
                    {notificationsCount > 0 && <Badge variant="glow" className="absolute -top-1 -right-1 w-6 h-6 p-0 text-xs border-2 border-background">
                        {notificationsCount > 99 ? '99+' : notificationsCount}
                      </Badge>}
                  </Button>
                </Link>

                {/* Messages */}
                <Link to="/app/messages">
                  <Button size="sm" variant="floating" className="relative w-12 h-12 rounded-2xl glass hover:glass-strong border border-border/30 hover:border-secondary/50 hover:shadow-secondary transition-all duration-300 group">
                    <MessageCircle className="w-5 h-5 transition-transform group-hover:scale-110" />
                    {messagesCount > 0 && <Badge variant="secondary" className="absolute -top-1 -right-1 w-6 h-6 p-0 text-xs border-2 border-background">
                        {messagesCount > 99 ? '99+' : messagesCount}
                      </Badge>}
                  </Button>
                </Link>

                {/* Search Button - Solo Mobile */}
                <Button variant="floating" size="sm" className="md:hidden w-12 h-12 rounded-2xl glass hover:glass-strong border border-border/30 hover:border-primary/50 transition-all duration-300">
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Mobile Search Bar */}
            <div className="md:hidden px-4 pb-4">
              <GlobalSearch placeholder={t('search.mobile')} className="w-full" />
            </div>
          </header>

          {/* Page Content with Ads Sidebar */}
          <div className="flex flex-1 overflow-hidden">
            <main className="flex-1 overflow-auto pb-20 md:pb-0 min-w-0">
              {children}
            </main>
            
          </div>
        </div>
      </div>


      {/* Bottom Navigation Toggle Button - Linguetta centrale */}
      <div className={`fixed bottom-0 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${isNavbarVisible ? 'translate-y-0' : '-translate-y-2'}`}>
        <button onClick={() => setIsNavbarVisible(!isNavbarVisible)} className="relative w-16 h-8 rounded-t-2xl border-t border-l border-r border-primary/20 text-foreground transition-all duration-300 hover:scale-105 group">
          <div className="relative flex items-center justify-center h-full my-[3px] py-[5px]">
            {isNavbarVisible ? <X className="w-4 h-4 transition-all duration-300 group-hover:scale-110" /> : <Menu className="w-4 h-4 transition-all duration-300 group-hover:scale-110" />}
          </div>
          {/* Indicatore stato */}
          <div className={`absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full transition-all duration-300 ${isNavbarVisible ? 'bg-green-400 glow-green' : 'bg-orange-400 glow-orange'}`} />
        </button>
      </div>

      {/* Bottom Navigation - Mobile */}
      <nav className={`fixed bottom-0 left-0 right-0 z-40 glass-strong border-t border-border/20 shadow-elevated backdrop-blur-2xl transition-transform duration-300 ${isNavbarVisible ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="overflow-x-auto overflow-y-hidden scrollbar-hide">
          <div className="flex items-center gap-2 px-4 min-w-max py-px">
            {navigation.map((item, index) => <Link key={item.name} to={item.href} className={`group flex flex-col items-center flex-shrink-0 p-3 rounded-2xl transition-all duration-300 hover:scale-105 min-w-[72px] ${isActive(item.href) ? "text-primary scale-110" : "text-muted-foreground hover:text-foreground"}`}>
                <div className={`relative p-3 rounded-2xl transition-all duration-300 ${isActive(item.href) ? "gradient-primary shadow-primary scale-110" : "glass group-hover:glass-strong group-hover:scale-105"}`}>
                  {/* Se è il profilo, mostra l'avatar invece dell'icona */}
                  {item.name === t('nav.profile') ? <Avatar className="w-6 h-6 ring-2 ring-current">
                      <AvatarImage src={user?.user_metadata?.avatar_url} />
                      <AvatarFallback className="gradient-primary text-white font-bold text-xs">
                        {profile?.display_name?.charAt(0).toUpperCase() || profile?.username?.charAt(0).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar> : <item.icon className="w-6 h-6" />}
                  {/* Badge per messaggi e notifiche */}
                  {item.name === t('nav.messages') && messagesCount > 0 && <Badge variant="secondary" className="absolute -top-1 -right-1 w-5 h-5 p-0 text-xs border-2 border-background">
                      {messagesCount > 99 ? '99+' : messagesCount}
                    </Badge>}
                </div>
                <span className={`text-xs mt-1.5 font-medium transition-all whitespace-nowrap ${isActive(item.href) ? "text-primary font-semibold" : ""}`}>
                  {item.name}
                </span>
              </Link>)}
          </div>
        </div>
      </nav>
    </SidebarProvider>;
};
export default NewMainLayout;